import { useMemo, useState } from 'react';
import { ChevronRight, ChevronLeft, Check, Play, Waypoints } from 'lucide-react';
import type { Technique, StatusMap } from '../types';
import { TechniqueStatus } from '../types';
import { series, resolveSeries } from '../data/series';
import { TIER_LABELS, TIER_ACCENT_COLORS } from '../constants/layout';
import TechniqueRow from './TechniqueRow';
import ViewHeader from './ViewHeader';

interface JourneyProps {
  techniques: Technique[];
  statusMap: StatusMap;
  watchedUrls: Set<string>;
  onPlay: (tech: Technique) => void;
  onCycle: (tech: Technique) => void;
  onSetStatus: (id: string, status: TechniqueStatus) => void;
}

// Horizontal offsets for the winding "Duolingo" path — a gentle zigzag that
// cycles as you scroll down the lesson nodes.
const PATH_OFFSETS = [0, 34, 52, 34, 0, -34, -52, -34];

export default function Journey({ techniques, statusMap, watchedUrls, onPlay, onSetStatus }: JourneyProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openNode, setOpenNode] = useState<string | null>(null);

  // Resolve every series against the live pool once.
  const resolved = useMemo(() =>
    series
      .map((s) => ({ s, techs: resolveSeries(s, techniques) }))
      .filter((r) => r.techs.length > 0),
    [techniques]
  );

  const selected = selectedId ? resolved.find((r) => r.s.id === selectedId) ?? null : null;

  // ─── Journey selection screen ───
  if (!selected) {
    return (
      <div className="journey-view">
        <ViewHeader
          bleed
          icon={<Waypoints size={38} strokeWidth={1.75} />}
          title="Series"
          subtitle="Choose a series to develop your game how you want."
        />
        <div className="journey-pick-list">
          {resolved.map(({ s, techs }) => {
            const total = techs.length;
            const done = techs.filter((t) => statusMap[t.id] === TechniqueStatus.Completed).length;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <button key={s.id} className="journey-pick-card" onClick={() => { setSelectedId(s.id); setOpenNode(null); }}>
                <div className="journey-pick-info">
                  <span className="journey-pick-title">{s.title}</span>
                  {s.blurb && <span className="journey-pick-blurb">{s.blurb}</span>}
                  <div className="journey-pick-bar">
                    <div className="journey-pick-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="journey-pick-right">
                  <span className="journey-pick-count">{done}/{total}</span>
                  <ChevronRight size={18} className="journey-pick-chevron" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Path screen for the selected journey ───
  const { s, techs } = selected;
  const total = techs.length;
  const done = techs.filter((t) => statusMap[t.id] === TechniqueStatus.Completed).length;
  const nextId = techs.find((t) => statusMap[t.id] !== TechniqueStatus.Completed)?.id ?? null;

  return (
    <div className="journey-view">
      <button className="journey-back" onClick={() => { setSelectedId(null); setOpenNode(null); }}>
        <ChevronLeft size={16} /> All series
      </button>

      <div className="journey-path-head">
        <h2 className="journey-path-title">{s.title}</h2>
        {s.blurb && <p className="journey-path-blurb">{s.blurb}</p>}
        <span className="journey-path-count">{done} of {total} complete</span>
      </div>

      <div className="journey-path">
        {techs.map((t, i) => {
          const status = statusMap[t.id] || TechniqueStatus.Unchecked;
          const isDone = status === TechniqueStatus.Completed;
          const isNext = t.id === nextId;
          const offset = PATH_OFFSETS[i % PATH_OFFSETS.length];
          const accent = TIER_ACCENT_COLORS[t.tier];

          // Belt-tier landmark banner whenever the tier changes.
          const isFirstOfTier = i === 0 || techs[i - 1].tier !== t.tier;
          const banner = isFirstOfTier ? (
            <div className="journey-landmark" key={`landmark-${t.tier}`}>
              <span className="journey-landmark-line" style={{ background: accent }} />
              <span className="journey-landmark-label" style={{ color: accent, borderColor: accent }}>
                {TIER_LABELS[t.tier]}
              </span>
              <span className="journey-landmark-line" style={{ background: accent }} />
            </div>
          ) : null;

          const nodeClass = `journey-node${isDone ? ' journey-node--done' : isNext ? ' journey-node--next' : ' journey-node--todo'}`;
          const open = openNode === t.id;

          return (
            <div className="journey-step" key={t.id}>
              {banner}
              <div className="journey-node-row" style={{ transform: `translateX(${offset}px)` }}>
                <button
                  className={nodeClass}
                  style={isNext ? { borderColor: accent } : isDone ? { background: accent, borderColor: accent } : undefined}
                  onClick={() => setOpenNode(open ? null : t.id)}
                  aria-label={t.name}
                >
                  {isDone ? <Check size={20} /> : isNext ? <Play size={18} /> : <span className="journey-node-num">{i + 1}</span>}
                  {isNext && <span className="journey-node-pulse" style={{ borderColor: accent }} />}
                </button>
                <span className="journey-node-name">{t.name}</span>
              </div>

              {open && (
                <div className="journey-node-pop">
                  <TechniqueRow
                    tech={t}
                    status={status}
                    watched={t.instagramUrl ? watchedUrls.has(t.instagramUrl) : false}
                    badge={isNext ? 'Up next' : undefined}
                    onPlay={onPlay}
                    onCycle={() => onSetStatus(t.id, isDone ? TechniqueStatus.Unchecked : TechniqueStatus.Completed)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
